import { useState, useRef, useCallback, useEffect } from 'react';
import { fetchToken } from '../services/salesDemoService';

export type AgentState = 'listening' | 'talking' | 'thinking' | null;
export type SessionStatus = 'idle' | 'connecting' | 'active' | 'ending' | 'done' | 'error';

export interface ConversationTurn {
  role: 'user' | 'assistant';
  text: string;
  timestamp: number;
}

export interface VoiceSessionState {
  status: SessionStatus;
  secondsRemaining: number;
  transcript: ConversationTurn[];
  agentState: AgentState;
  error: string | null;
}

const INITIAL_STATE: VoiceSessionState = {
  status: 'idle',
  secondsRemaining: 300,
  transcript: [],
  agentState: null,
  error: null,
};

const SESSION_DURATION_SECONDS = 300;

export function useVoiceSession() {
  const [state, setState] = useState<VoiceSessionState>(INITIAL_STATE);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionStartRef = useRef<number>(0);
  const transcriptRef = useRef<ConversationTurn[]>([]);
  const inputAnalyserRef = useRef<AnalyserNode | null>(null);
  const outputAnalyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const inputVolumeRef = useRef<number>(0);
  const outputVolumeRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioBlobRef = useRef<Blob | null>(null);
  const languageRef = useRef<string>('en');

  const stopAnimationFrame = useCallback(() => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, []);

  const startVolumeLoop = useCallback(() => {
    const tick = () => {
      if (inputAnalyserRef.current) {
        const data = new Uint8Array(inputAnalyserRef.current.frequencyBinCount);
        inputAnalyserRef.current.getByteFrequencyData(data);
        const sum = data.reduce((a, b) => a + b, 0);
        inputVolumeRef.current = sum / data.length / 255;
      }
      if (outputAnalyserRef.current) {
        const data = new Uint8Array(outputAnalyserRef.current.frequencyBinCount);
        outputAnalyserRef.current.getByteFrequencyData(data);
        const sum = data.reduce((a, b) => a + b, 0);
        outputVolumeRef.current = sum / data.length / 255;
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  const getInputVolume = useCallback((): number => {
    return inputVolumeRef.current;
  }, []);

  const getOutputVolume = useCallback((): number => {
    return outputVolumeRef.current;
  }, []);

  const appendTranscript = useCallback((role: 'user' | 'assistant', text: string) => {
    if (!text?.trim()) return;
    const turn: ConversationTurn = { role, text: text.trim(), timestamp: Date.now() };
    transcriptRef.current = [...transcriptRef.current, turn];
    setState(prev => ({ ...prev, transcript: transcriptRef.current }));
  }, []);

  const handleDataChannelEvent = useCallback(
    (msg: Record<string, unknown>) => {
      switch (msg.type) {
        case 'input_audio_buffer.speech_started':
          setState(prev => ({ ...prev, agentState: 'listening' }));
          break;
        case 'input_audio_buffer.speech_stopped':
          setState(prev => ({ ...prev, agentState: 'thinking' }));
          break;
        case 'output_audio_buffer.started':
          setState(prev => ({ ...prev, agentState: 'talking' }));
          break;
        case 'output_audio_buffer.stopped':
          setState(prev => ({ ...prev, agentState: null }));
          break;
        case 'conversation.item.input_audio_transcription.completed':
          console.log('[DC] user transcription:', msg);
          appendTranscript('user', (msg.transcript as string) ?? '');
          break;
        case 'response.output_audio_transcript.done':
          console.log('[DC] assistant transcript:', msg);
          appendTranscript('assistant', (msg.transcript as string) ?? '');
          break;
        // Also capture text deltas for debugging
        case 'response.output_text.done':
          console.log('[DC] output text done:', msg);
          break;

        // Tool call: AI decided to end the interview
        case 'response.function_call_arguments.done': {
          const fnName = msg.name as string;
          console.log(`[DC] function call: ${fnName}`, msg.arguments);

          if (fnName === 'end_interview') {
            // Acknowledge the tool call so the AI knows it succeeded
            if (dataChannelRef.current?.readyState === 'open') {
              dataChannelRef.current.send(JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: msg.call_id as string,
                  output: JSON.stringify({ success: true }),
                },
              }));
            }
            // End the session after a short delay for final audio
            console.log('[DC] agent called end_interview — ending session');
            setTimeout(() => endSession(), 1000);
          }
          break;
        }

        case 'session.created':
          console.log('[DC] session.created — configuring session');
          if (dataChannelRef.current?.readyState === 'open') {
            // 1) Enable input transcription (must work — this is what made it work in 29b3b75)
            dataChannelRef.current.send(JSON.stringify({
              type: 'session.update',
              session: {
                type: 'realtime',
                audio: {
                  input: {
                    transcription: {
                      model: 'gpt-4o-transcribe',
                      language: languageRef.current,
                    },
                  },
                },
              },
            }));
            console.log('[DC] sent session.update for transcription');

            // 2) Configure VAD + tools in a separate update
            dataChannelRef.current.send(JSON.stringify({
              type: 'session.update',
              session: {
                type: 'realtime',
                audio: {
                  input: {
                    turn_detection: {
                      type: 'server_vad',
                      threshold: 0.9,
                      silence_duration_ms: 1500,
                      prefix_padding_ms: 300,
                    },
                  },
                },
                tools: [
                  {
                    type: 'function',
                    name: 'end_interview',
                    description: 'Call this function when the interview is complete — either because you have asked enough questions to evaluate all 5 dimensions, or because the candidate explicitly asks to end the interview. This will close the voice session and trigger the scoring.',
                    parameters: {
                      type: 'object',
                      properties: {
                        reason: {
                          type: 'string',
                          description: 'Why the interview is ending: "questions_complete" or "candidate_requested"',
                        },
                      },
                    },
                  },
                ],
              },
            }));
            console.log('[DC] sent session.update for VAD + tools');

            // 3) Make the agent speak first
            dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
            console.log('[DC] sent response.create — agent will speak first');
          }
          break;
        case 'session.updated':
          console.log('[DC] session.updated:', msg);
          break;
        case 'error': {
          const err = msg.error as Record<string, unknown> | undefined;
          console.error('[DC] server error:', {
            code: err?.code,
            message: err?.message,
          });
          break;
        }
        default:
          console.log('[DC] unhandled event:', msg.type);
          break;
      }
    },
    [appendTranscript],
  );

  const getAudioBlob = useCallback((): Blob | null => {
    return audioBlobRef.current;
  }, []);

  const endSession = useCallback(() => {
    setState(prev => ({ ...prev, status: 'ending' }));

    // Stop MediaRecorder and finalize blob
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    stopAnimationFrame();

    inputVolumeRef.current = 0;
    outputVolumeRef.current = 0;

    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {
        // ignore close errors
      });
      audioContextRef.current = null;
    }

    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }

    inputAnalyserRef.current = null;
    outputAnalyserRef.current = null;

    // Build final audio blob from collected chunks
    if (audioChunksRef.current.length > 0) {
      audioBlobRef.current = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    }

    setState(prev => ({ ...prev, status: 'done', agentState: null }));
  }, [stopAnimationFrame]);

  const startSession = useCallback(
    async (jobDescription: string, accessCode: string, language: string = 'en') => {
      setState({
        ...INITIAL_STATE,
        status: 'connecting',
      });
      transcriptRef.current = [];
      audioChunksRef.current = [];
      audioBlobRef.current = null;
      languageRef.current = language;

      try {
        // Step 1: Fetch ephemeral token AT CLICK TIME — not on mount
        const { token, webrtc_url } = await fetchToken(jobDescription, accessCode, language);

        // Step 2: Get microphone access with echo cancellation
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 24000,
          },
        });
        micStreamRef.current = stream;

        // Step 2b: Start recording mic audio for post-session transcription
        const recorder = new MediaRecorder(stream, {
          mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm',
        });
        mediaRecorderRef.current = recorder;
        audioChunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            audioChunksRef.current.push(e.data);
          }
        };

        recorder.start(1000); // collect chunks every second

        // Step 3: Create RTCPeerConnection
        const pc = new RTCPeerConnection();
        peerConnectionRef.current = pc;

        // Add mic tracks to peer connection
        stream.getTracks().forEach(track => pc.addTrack(track, stream));

        // Step 4: Wire remote audio to browser speaker
        const audioEl = new Audio();
        audioEl.autoplay = true;
        audioElementRef.current = audioEl;

        pc.ontrack = event => {
          audioEl.srcObject = event.streams[0];

          if (audioContextRef.current) {
            try {
              const outputSource = audioContextRef.current.createMediaStreamSource(
                event.streams[0],
              );
              const outputAnalyser = audioContextRef.current.createAnalyser();
              outputAnalyser.fftSize = 256;
              outputSource.connect(outputAnalyser);
              outputAnalyserRef.current = outputAnalyser;
            } catch {
              // Output volume measurement unavailable
            }
          }
        };

        // Step 5: Create data channel BEFORE creating SDP offer (critical ordering)
        const dc = pc.createDataChannel('realtime-channel');
        dataChannelRef.current = dc;

        dc.addEventListener('open', () => {
          console.log('[DC] data channel open');

          sessionStartRef.current = Date.now();
          setState(prev => ({ ...prev, status: 'active', secondsRemaining: SESSION_DURATION_SECONDS }));

          timerRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
            const remaining = Math.max(0, SESSION_DURATION_SECONDS - elapsed);
            setState(prev => ({ ...prev, secondsRemaining: remaining }));
            if (remaining === 0) {
              endSession();
            }
          }, 1000);
        });

        dc.addEventListener('message', event => {
          try {
            const msg = JSON.parse(event.data as string) as Record<string, unknown>;
            handleDataChannelEvent(msg);
          } catch {
            // Ignore malformed data channel messages
          }
        });

        dc.addEventListener('close', () => {
          setState(prev => ({ ...prev, agentState: null }));
        });

        // Step 6: Create SDP offer and set local description
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // Step 7: POST SDP offer to Azure WebRTC endpoint with ephemeral token
        const sdpResponse = await fetch(webrtc_url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        });

        if (!sdpResponse.ok) {
          throw new Error(`SDP exchange failed: ${sdpResponse.status}`);
        }

        const answerSdp = await sdpResponse.text();
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

        // Step 8: Set up Web Audio API AnalyserNode for input volume
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        const inputSource = audioContext.createMediaStreamSource(stream);
        const inputAnalyser = audioContext.createAnalyser();
        inputAnalyser.fftSize = 256;
        inputSource.connect(inputAnalyser);
        inputAnalyserRef.current = inputAnalyser;

        // Step 9: Start RAF loop for continuous volume measurement
        startVolumeLoop();
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        let errorMessage: string;

        if (message.includes('Invalid access code') || message.includes('Unauthorized') || message.includes('401')) {
          errorMessage = 'Invalid access code — please check your access code and try again';
        } else if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('notallowederror')) {
          errorMessage = 'Please allow microphone access and try again';
        } else if (message.includes('SDP exchange failed')) {
          errorMessage = 'Could not start voice session — please try again';
        } else if (message.includes('fetch') || message.includes('Failed to fetch') || message.includes('NetworkError')) {
          errorMessage = 'Could not connect to server — please check your connection and try again';
        } else if (message.includes('Token request failed') || message.includes('502')) {
          errorMessage = 'Could not connect to voice service — please try again';
        } else {
          errorMessage = 'Could not start voice session — please try again';
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
        if (micStreamRef.current) {
          micStreamRef.current.getTracks().forEach(t => t.stop());
          micStreamRef.current = null;
        }
        if (peerConnectionRef.current) {
          peerConnectionRef.current.close();
          peerConnectionRef.current = null;
        }
        stopAnimationFrame();

        setState(prev => ({
          ...prev,
          status: 'error',
          error: errorMessage,
        }));
      }
    },
    [endSession, handleDataChannelEvent, startVolumeLoop],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      stopAnimationFrame();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(t => t.stop());
      }
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
          // ignore
        });
      }
    };
  }, [stopAnimationFrame]);

  const resetSession = useCallback(() => {
    setState(INITIAL_STATE);
    transcriptRef.current = [];
    audioChunksRef.current = [];
    audioBlobRef.current = null;
  }, []);

  return {
    state,
    startSession,
    endSession,
    resetSession,
    getInputVolume,
    getOutputVolume,
    getAudioBlob,
  };
}
